import React, { useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { useAppState } from "Context/StateContext";
import TransshipmentService from "Services/transshipment.service";
import { CACHE_TIME } from "Util/constants";
import SkeletonDetails from "../../../../../../components/Generic/LoadingSkeleton";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../../../components/Generic/ViewJobComponent";
import { Order } from "../../../../../../models/order";
import { ItemJob } from "../../../../../../models/sales";
import { Transshipment, TransshipmentLocation } from "../../../../../../models/transshipment";
import { CommentsTab } from "./Comments";
import { DetailsTab } from "./Details";
import { OrdersTab } from "./Orders";

interface Props {
  transshipment?: Transshipment;
  sideViewRef: React.RefObject<PropHandles>;
  loading: boolean;
}

export const TransshipmentViewModal = ({ transshipment, sideViewRef, loading }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser, warehouse } = useAppState();
  const [jobs, setJobs] = useState<Order[]>([]);

  const id = transshipment?.id;
  const attributes = useMemo(
    () => ({
      leader: ["id", "fullName"],
      warehouse: ["id", "name"],
      commentPicture: ["id", "mediaLink"],
      jobCustomer: ["fullName"],
      jobBranchFrom: ["name"],
      jobToBranch: ["name"],
      itemJobRoom: ["id", "name"],
      itemJobItemType: ["id", "name"],
      itemJobSymbol: ["id", "name"],
    }),
    []
  );

  const { data, isFetching, error } = useQuery({
    queryKey: ["transshipments.detail", currentUser?.companyId, warehouse?.id, Number(id)],
    queryFn: () =>
      TransshipmentService.getById(Number(id), {
        include: JSON.stringify([
          {
            association: "transshipmentLocations",
            include: [
              { association: "fromStorageBox", include: [{ association: "storageBoxType" }] },
              { association: "toStorageBox", include: [{ association: "storageBoxType" }] },
              {
                association: "fromMapCell",
                include: [
                  {
                    association: "map",
                    attributes: ["id"],
                    include: [{ association: "section", attributes: ["name"] }],
                  },
                ],
              },
              {
                association: "toMapCell",
                include: [
                  {
                    association: "map",
                    attributes: ["id"],
                    include: [{ association: "section", attributes: ["name"] }],
                  },
                ],
              },
              {
                association: "itemJob",
                include: [
                  {
                    association: "job",
                    include: [
                      { association: "customer", attributes: attributes.jobCustomer },
                      { association: "branchFrom", attributes: attributes.jobBranchFrom },
                      { association: "toBranch", attributes: attributes.jobToBranch },
                    ],
                  },
                  { association: "itemType", attributes: attributes.itemJobItemType },
                  { association: "storageBoxes" },
                  { association: "room", attributes: attributes.itemJobRoom },
                  { association: "symbol", attributes: attributes.itemJobSymbol },
                  {
                    association: "exceptions",
                    include: [
                      { association: "exceptionSymbol" },
                      { association: "locationSymbol" },
                    ],
                  },
                ],
              },
            ],
          },
          { association: "leader", attributes: attributes.leader },
          { association: "warehouse", attributes: attributes.warehouse },
          {
            association: "comments",
            include: [
              {
                association: "createdBy",
                include: [{ association: "picture", attributes: attributes.commentPicture }],
              },
            ],
          },
        ]),
      }),
    enabled: !!currentUser?.companyId && !!warehouse?.id && !!id,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: CACHE_TIME,
    onSuccess: (response: any) => {
      if (response?.data) {
        const transshipmentLocationsData = response.data.transshipmentLocations as Array<TransshipmentLocation>;
        handleJobs(transshipmentLocationsData);
      }
    },
    onError: (error) => {
      console.error("Failed to fetch transshipment:", error);
    },
  });

  const handleJobs = useCallback(
    (transshipmentLocationsData: Array<TransshipmentLocation>) => {
      const newJobs: Order[] = [];
      transshipmentLocationsData?.forEach((location) => {
        const jobRegisteredIndex = newJobs?.findIndex((job) => job?.id === location?.itemJob?.jobId);

        if (jobRegisteredIndex !== -1) {
          newJobs[jobRegisteredIndex]?.itemsJob?.push({
            ...location?.itemJob,
            job: undefined,
          } as ItemJob);
        } else {
          newJobs.push({
            ...location?.itemJob?.job,
            itemsJob: [{ ...location?.itemJob, job: undefined }],
          } as Order);
        }
      });
      setJobs(newJobs); 
    },
    [] 
  );

  const handleSetTabs = useCallback(() => {
    const tabs = [
      {
        key: Navigation.DETAILS,
        loading,
        isEmpty: !transshipment,
        render: () => <DetailsTab transshipment={data?.data || transshipment} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.ORDERS,
        loading: isFetching,
        isEmpty: !jobs.length,
        render: () => <OrdersTab transshipmentJobs={jobs} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.COMMENTS,
        loading,
        isEmpty: !data?.data?.comments || data?.data?.comments?.length === 0,
        render: () => <CommentsTab comments={data?.data?.comments || data?.data?.comments} />, // Dùng dữ liệu từ query nếu có
        renderLoading: () => <SkeletonDetails />,
      },
    ];
    return tabs;
  }, [transshipment, jobs, loading, isFetching, data]);

  const handleSetNavigation = useCallback(() => {
    return [
      { key: Navigation.DETAILS },
      { key: Navigation.ORDERS },
      { key: Navigation.COMMENTS },
    ];
  }, []);

  const getTitle = useCallback(() => {
    if (transshipment?.reference) {
      return `${translate("transshipment")} #${transshipment?.reference}`;
    }
    return translate("transshipment");
  }, [translate, transshipment]);

  return (
    <SideViewComponent
      ref={sideViewRef}
      isLoading={loading}
      title={getTitle()}
      navigation={handleSetNavigation()}
      tabs={handleSetTabs()}
    />
  );
};